import CircularLoader from 'components/CircularLoader';
import { Close as CloseIcon, Search as SearchIcon } from 'components/icons';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import {
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxList,
} from 'components/ui/combobox';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from 'components/ui/input-group';
import get from 'lodash/get';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldValues, Path, PathValue, useWatch } from 'react-hook-form';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { MinDto } from 'types/other';
import { groupConsecutiveOptions, OptionGroup } from 'utils/autocompleteGrouping';
import { t } from 'utils/getTranslations';
import { PickerModalProps } from '../../form-components/select-picker/DynamicMultiSelectPicker';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useMemoizedRules } from './useMemoizedRules';
import { PaginatedListProps, usePaginatedPopup } from './usePaginatedList';

type GetOptionIdentifier<TOption> = ((option: TOption) => string | number) | string;
type GetOptionLabel<TOption> = ((option: TOption) => string) | string;

interface TableMultiSelectInputProps<
    TFormData extends FieldValues,
    TOption extends MinDto,
    TPickedOption extends MinDto = TOption,
    TCreatedOption extends MinDto = TOption,
> extends Omit<CommonInputProps<TFormData>, 'onChange'> {
    control: Control<TFormData>;
    setValue: (name: Path<TFormData>, value: PathValue<TFormData, Path<TFormData>>) => void;
    getOptionLabel: GetOptionLabel<TOption>;
    getOptionIdentifier: GetOptionIdentifier<TOption>;
    paginated: PaginatedListProps<TOption>;
    PickerModalComponent?: React.ComponentType<PickerModalProps<TPickedOption, TCreatedOption>>;
    isPickerModalOptionEnabled?: (option: TPickedOption) => boolean;
    readonly?: boolean | { linkTo: string };
    onChange?: (value: TOption[]) => void;
    /** Function to group options in the dropdown. Returns group name or null for ungrouped. */
    groupByStaticFields?: (option: TOption) => string | null;
    noOptionsContent?: React.ReactNode;
    /** When true, the dropdown stays open after each selection, allowing multiple picks without reopening. */
    disableCloseOnSelect?: boolean;
}

const ROW_HEIGHT = 40;
const MAX_VISIBLE_ROWS = 8;
const TABLE_MAX_HEIGHT = ROW_HEIGHT * MAX_VISIBLE_ROWS;

const TableMultiSelectInput = <
    TFormData extends FieldValues,
    TOption extends MinDto,
    TPickedOption extends MinDto = TOption,
    TCreatedOption extends MinDto = TOption,
>({
    name,
    label,
    shouldTranslateLabel = true,
    required,
    tooltip,
    rightLabelComponent,
    disabled,
    placeholder,
    shouldTranslatePlaceholder = true,
    validate,
    wrapperClassName,
    control,
    setValue,
    getOptionLabel,
    getOptionIdentifier,
    paginated,
    PickerModalComponent,
    isPickerModalOptionEnabled,
    readonly = false,
    onChange,
    groupByStaticFields,
    noOptionsContent,
    disableCloseOnSelect = false,
}: TableMultiSelectInputProps<TFormData, TOption, TPickedOption, TCreatedOption>) => {
    const rules = useMemoizedRules({ required, validate });
    const watchedValue = useWatch({ control, name }) as TOption[] | undefined;
    const { openModal, closeModal } = useModal();
    const [inputValue, setInputValue] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const [lastAddedId, setLastAddedId] = useState<string | number | null>(null);
    const listRef = useRef<FixedSizeList>(null);

    const {
        dataToDisplay,
        handleSearchChange,
        isLoading,
        isOpen,
        isOpenPending,
        setOpen,
        dropPendingOpen,
        onInputKeyDown,
    } = usePaginatedPopup<TOption>(paginated, () => setInputValue(''));

    const getLabel = useCallback(
        (option: TOption | null): string => {
            if (!option) return '';
            if (typeof getOptionLabel === 'string') {
                return get(option, getOptionLabel) ?? '';
            }
            return getOptionLabel(option) ?? '';
        },
        [getOptionLabel]
    );

    const getValue = useCallback(
        (option: TOption | null): string | number | null => {
            if (!option) return null;
            if (typeof getOptionIdentifier === 'string') {
                return get(option, getOptionIdentifier);
            }
            return getOptionIdentifier(option);
        },
        [getOptionIdentifier]
    );

    const getGroupedBy = useCallback(
        (option: TOption): string => {
            if (groupByStaticFields) {
                return groupByStaticFields(option) ?? t('field.other');
            }
            return '';
        },
        [groupByStaticFields]
    );

    const handleAddItem = useCallback(
        (newItem: TOption) => {
            const currentValue = watchedValue || [];
            const newItemId = getValue(newItem);
            const alreadyExists = currentValue.some((item) => getValue(item) === newItemId);
            if (!alreadyExists) {
                const newValue = [...currentValue, newItem];
                setValue(name, newValue as PathValue<TFormData, Path<TFormData>>);
                onChange?.(newValue);
                setLastAddedId(newItemId);
                setFilterValue(''); // Clear filter to show the new item
            }
            if (!disableCloseOnSelect) {
                setInputValue('');
                handleSearchChange('');
            }
        },
        [watchedValue, setValue, name, getValue, onChange, disableCloseOnSelect, handleSearchChange]
    );

    const handleRemoveItem = useCallback(
        (itemToRemove: TOption) => {
            const currentValue = watchedValue || [];
            const newValue = currentValue.filter((item) => getValue(item) !== getValue(itemToRemove));
            setValue(name, newValue as PathValue<TFormData, Path<TFormData>>);
            onChange?.(newValue);
        },
        [watchedValue, setValue, name, getValue, onChange]
    );

    const handleOpenModal = useCallback(() => {
        if (!PickerModalComponent) return;

        const handleConfirm = (selectedItem: TPickedOption | TCreatedOption) => {
            // Cast to TOption since TPickedOption and TCreatedOption extend MinDto like TOption
            handleAddItem(selectedItem as unknown as TOption);
            closeModal();
        };

        openModal(
            <PickerModalComponent
                onClose={closeModal}
                onConfirm={handleConfirm}
                onCreateNew={handleConfirm}
                searchValue={inputValue}
                currentValue={watchedValue as unknown as TPickedOption[]}
                isOptionEnabled={isPickerModalOptionEnabled}
            />,
            closeModal,
            { width: '90vw', maxWidth: '1200px' }
        );
    }, [
        PickerModalComponent,
        openModal,
        closeModal,
        handleAddItem,
        inputValue,
        watchedValue,
        isPickerModalOptionEnabled,
    ]);

    // Filter out already selected items from the dropdown options
    const filteredOptions = dataToDisplay.filter(
        (option) => !(watchedValue || []).some((selected) => getValue(selected) === getValue(option))
    );

    const groupedOptions = useMemo<OptionGroup<TOption>[]>(() => {
        if (!groupByStaticFields) {
            return [];
        }
        return groupConsecutiveOptions(filteredOptions, getGroupedBy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredOptions, groupByStaticFields, getGroupedBy]);

    const selectedItems = useMemo(() => watchedValue || [], [watchedValue]);

    const filteredSelectedItems = useMemo(() => {
        if (!filterValue.trim()) return selectedItems;
        const lowerFilter = filterValue.toLowerCase();
        return selectedItems.filter((item) => getLabel(item).toLowerCase().includes(lowerFilter));
    }, [selectedItems, filterValue, getLabel]);

    // Scroll to the last item when a new item is added
    useEffect(() => {
        if (lastAddedId !== null && listRef.current && filteredSelectedItems.length > 0) {
            // Find the index of the last added item
            const lastAddedIndex = filteredSelectedItems.findIndex((item) => getValue(item) === lastAddedId);
            if (lastAddedIndex !== -1) {
                listRef.current.scrollToItem(lastAddedIndex, 'center');
            }
        }
    }, [lastAddedId, filteredSelectedItems, getValue]);

    // Clear highlight after animation
    useEffect(() => {
        if (lastAddedId !== null) {
            const timer = setTimeout(() => {
                setLastAddedId(null);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [lastAddedId]);

    // Virtualized row renderer
    const renderRow = ({ index, style, data }: ListChildComponentProps<TOption[]>) => {
        const item = data[index];
        const itemId = getValue(item);

        return (
            // The just-added row is painted, then fades as the flag clears — a colour transition
            // rather than a keyframe animation, so the fade needs no stylesheet of its own.
            <div
                role="row"
                style={style}
                data-recent={itemId === lastAddedId || undefined}
                className="hover:bg-muted/50 data-recent:bg-info-lighter flex items-center border-none transition-colors duration-[1500ms]"
            >
                <div
                    role="cell"
                    title={getLabel(item)}
                    className="flex-1 overflow-hidden px-4 py-2 text-sm text-ellipsis whitespace-nowrap"
                >
                    {getLabel(item)}
                </div>
                {!readonly && (
                    <div role="cell" className="w-12.5 px-2 py-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleRemoveItem(item)}
                            disabled={disabled}
                            aria-label={t('remove')}
                        >
                            <CloseIcon fontSize="small" />
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const listHeight = Math.min(filteredSelectedItems.length * ROW_HEIGHT, TABLE_MAX_HEIGHT);

    const renderSelectedList = () =>
        filteredSelectedItems.length > 0 ? (
            <FixedSizeList
                ref={listRef}
                height={listHeight}
                width="100%"
                itemCount={filteredSelectedItems.length}
                itemSize={ROW_HEIGHT}
                itemData={filteredSelectedItems}
                overscanCount={5}
            >
                {renderRow}
            </FixedSizeList>
        ) : (
            <div className="text-fg-secondary py-4 text-center text-sm">{t('no-matches-found')}</div>
        );

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { ref }, fieldState: { error } }) => (
                <InputWrapper
                    name={name}
                    label={label}
                    shouldTranslateLabel={shouldTranslateLabel}
                    tooltip={tooltip}
                    required={required}
                    readonly={!!readonly}
                    rightLabelComponent={rightLabelComponent}
                    hasErrors={!!error}
                    error={readonly ? undefined : error}
                    className={wrapperClassName}
                >
                    {readonly ? (
                        <div>
                            {selectedItems.length > 0 ? (
                                <div role="table" className="border-divider overflow-hidden rounded-sm border">
                                    <div role="row" className="bg-grey-50 flex items-center">
                                        <div role="columnheader" className="w-20 px-4 py-2 text-sm font-bold">
                                            {t('name')}
                                        </div>
                                        <div role="columnheader" className="flex-1 px-2 py-1">
                                            <InputGroup className="bg-paper h-8">
                                                <InputGroupAddon>
                                                    <SearchIcon fontSize="small" className="text-fg-secondary" />
                                                </InputGroupAddon>
                                                <InputGroupInput
                                                    placeholder={t('filter-selected')}
                                                    value={filterValue}
                                                    onChange={(e) => setFilterValue(e.target.value)}
                                                />
                                                {filterValue && (
                                                    <InputGroupAddon align="inline-end">
                                                        <InputGroupButton
                                                            size="icon-xs"
                                                            aria-label={t('clear')}
                                                            onClick={() => setFilterValue('')}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </InputGroupButton>
                                                    </InputGroupAddon>
                                                )}
                                            </InputGroup>
                                        </div>
                                    </div>
                                    {renderSelectedList()}
                                </div>
                            ) : (
                                <span className="text-fg-secondary text-sm">-</span>
                            )}
                        </div>
                    ) : (
                        <div>
                            <div className="mb-2 flex gap-2">
                                <div className="flex-1">
                                    <Combobox<TOption>
                                        items={
                                            (groupByStaticFields
                                                ? groupedOptions
                                                : filteredOptions) as readonly TOption[]
                                        }
                                        value={null}
                                        disabled={disabled}
                                        // Search runs on the server, so the popup shows exactly the page it was handed.
                                        filter={null}
                                        inputValue={inputValue}
                                        itemToStringLabel={(option) => getLabel(option)}
                                        isItemEqualToValue={(option, selectedOption) =>
                                            getValue(option) === getValue(selectedOption)
                                        }
                                        open={isOpen}
                                        onOpenChange={(open, details) => {
                                            // Picking a row closes the popup; `disableCloseOnSelect`
                                            // refuses that one close so several picks need one open.
                                            if (!open && disableCloseOnSelect && details.reason === 'item-press') {
                                                return;
                                            }
                                            setOpen(open, details);
                                        }}
                                        onValueChange={(newValue) => {
                                            if (newValue) {
                                                handleAddItem(newValue);
                                            }
                                        }}
                                        onInputValueChange={(value, details) => {
                                            if (details.reason !== 'input-change' && details.reason !== 'input-clear') {
                                                return;
                                            }
                                            setInputValue(value);
                                            handleSearchChange(value);
                                        }}
                                    >
                                        <ComboboxInput
                                            ref={ref}
                                            className="bg-paper"
                                            aria-invalid={!!error}
                                            aria-busy={isOpenPending || undefined}
                                            disabled={disabled}
                                            placeholder={
                                                placeholder
                                                    ? shouldTranslatePlaceholder
                                                        ? t(placeholder)
                                                        : placeholder
                                                    : t('search-to-add')
                                            }
                                            onKeyDown={onInputKeyDown}
                                            onBlur={dropPendingOpen}
                                        >
                                            {isOpenPending ? (
                                                <CircularLoader className="static mr-16 ml-auto group-has-[>[data-align=inline-end]>button]/input-group:mr-24 [&>span]:size-4" />
                                            ) : null}
                                        </ComboboxInput>
                                        <ComboboxContent className="shadow-dropdown">
                                            <ComboboxEmpty>
                                                {isLoading ? t('loading') : (noOptionsContent ?? t('no-options'))}
                                            </ComboboxEmpty>
                                            <ComboboxList>
                                                {groupByStaticFields
                                                    ? groupedOptions.map((group) => (
                                                          <ComboboxGroup key={group.key} items={group.items}>
                                                              <ComboboxLabel>{group.value}</ComboboxLabel>
                                                              <ComboboxCollection>
                                                                  {(option: TOption) => (
                                                                      <ComboboxItem
                                                                          key={getValue(option)}
                                                                          value={option}
                                                                          title={getLabel(option)}
                                                                      >
                                                                          {getLabel(option)}
                                                                      </ComboboxItem>
                                                                  )}
                                                              </ComboboxCollection>
                                                          </ComboboxGroup>
                                                      ))
                                                    : filteredOptions.map((option) => (
                                                          <ComboboxItem
                                                              key={getValue(option)}
                                                              value={option}
                                                              title={getLabel(option)}
                                                          >
                                                              {getLabel(option)}
                                                          </ComboboxItem>
                                                      ))}
                                            </ComboboxList>
                                        </ComboboxContent>
                                    </Combobox>
                                </div>
                                {PickerModalComponent && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleOpenModal}
                                        disabled={disabled}
                                        className="text-primary"
                                        aria-label={t('search')}
                                    >
                                        <SearchIcon />
                                    </Button>
                                )}
                            </div>

                            {selectedItems.length > 0 ? (
                                <div role="table" className="border-divider overflow-hidden rounded-sm border">
                                    <div role="row" className="bg-grey-50 flex items-center">
                                        <div role="columnheader" className="flex-1 px-4 py-2 text-sm font-bold">
                                            {t('name')}
                                        </div>
                                        <div role="columnheader" className="w-12.5 px-2 py-2" />
                                    </div>
                                    {renderSelectedList()}
                                </div>
                            ) : (
                                <div className="border-divider text-fg-secondary rounded-sm border border-dashed p-4 text-center text-sm">
                                    {t('no-items-selected')}
                                </div>
                            )}
                        </div>
                    )}
                </InputWrapper>
            )}
        />
    );
};

export default TableMultiSelectInput;
